import { Box, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import AssistantAvatar from './assistant-avatar';
import MessageBubble, { Bubble } from './message-bubble';
import TypingIndicator from './typing-indicator';

interface MessageListProps {
  bubbles: Bubble[];
  /** True from the moment of sending until the assistant starts to write. */
  isWaiting: boolean;
  /** What the assistant is doing while it waits: thinking, consulting a tool, analysing what it got. */
  status: string;
}

export default function MessageList({
  bubbles,
  isWaiting,
  status,
}: MessageListProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  // Scrolls the list itself. scrollIntoView would scroll every ancestor too, the page included,
  // and on iOS that moves the visible area the panel is sized to.
  useEffect(() => {
    const list = listRef.current;

    list?.scrollTo?.({ top: list.scrollHeight, behavior: 'smooth' });
  }, [bubbles.length, isWaiting, status]);

  // While an answer is being written it grows on every piece, and a smooth scroll would lag behind it.
  const lastTextLength = bubbles[bubbles.length - 1]?.text.length;
  useEffect(() => {
    const list = listRef.current;

    list?.scrollTo?.({ top: list.scrollHeight });
  }, [lastTextLength]);

  return (
    <Box
      ref={listRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {bubbles.length === 0 && (
        <Box
          sx={{
            m: 'auto',
            textAlign: 'center',
            color: 'text.secondary',
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <AssistantAvatar size={56} iconSize={24} />
          </Box>
          <Typography variant="body2">{t('ASSISTANT_EMPTY')}</Typography>
        </Box>
      )}
      {/* The messages sit against the input, so the latest stay in view when the keyboard hides the top. */}
      <Box
        sx={{
          mt: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {bubbles.map((bubble, index) => (
          <MessageBubble key={index} {...bubble} />
        ))}
        {isWaiting && (
          <TypingIndicator label={t('ASSISTANT_THINKING')} status={status} />
        )}
      </Box>
    </Box>
  );
}
